/**
 * Training Service
 * API integration for training CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de treinamentos
 */
import apiClient from './apiClient';
import {
  Training,
  TrainingFilters,
  TrainingFormData,
  TrainingApiPayload,
  TrainingListResponse,
  TrainingStatistics,
  TrainingStatus,
  TrainingNRType,
  TrainingCategory,
} from '../types/training';

// Re-export types for convenience
export * from '../types/training';

export interface TrainingListParams extends TrainingFilters {
  page?: number;
  limit?: number;
}

class TrainingService {
  private baseUrl = '/trainings';

  /**
   * Convert form data to API payload
   */
  private formDataToApiPayload(formData: TrainingFormData): TrainingApiPayload {
    return {
      employeeId: formData.employeeId,
      departmentId: formData.departmentId ?? undefined,
      occupationId: formData.occupationId ?? undefined,
      instructorId: formData.instructorId ?? undefined,
      trainingTypeId: formData.trainingTypeId ?? undefined,
      name: formData.name,
      type: formData.type,
      category: formData.category,
      description: formData.description ?? undefined,
      activityDescription: formData.activityDescription ?? undefined,
      workload: formData.workload ?? undefined,
      completedAt: formData.completedAt,
      expiresAt: formData.expiresAt,
      certificatePath: formData.certificatePath ?? undefined,
      esocialCode: formData.esocialCode ?? undefined,
      sendToEsocial: formData.sendToEsocial,
      esocialEventId: formData.esocialEventId ?? undefined,
      esocialStatus: formData.esocialStatus ?? undefined,
      status: formData.status,
    };
  }

  /**
   * Convert API response to form data
   */
  apiResponseToFormData(apiData: Training): TrainingFormData {
    return {
      employeeId: apiData.employeeId,
      departmentId: apiData.departmentId,
      occupationId: apiData.occupationId,
      instructorId: apiData.instructorId,
      trainingTypeId: apiData.trainingTypeId,
      name: apiData.name,
      type: apiData.type,
      category: apiData.category,
      description: apiData.description ?? '',
      activityDescription: apiData.activityDescription ?? '',
      workload: apiData.workload,
      completedAt: apiData.completedAt,
      expiresAt: apiData.expiresAt,
      certificatePath: apiData.certificatePath ?? '',
      esocialCode: apiData.esocialCode ?? '',
      sendToEsocial: apiData.sendToEsocial,
      esocialEventId: apiData.esocialEventId ?? '',
      esocialStatus: apiData.esocialStatus ?? '',
      status: apiData.status,
    };
  }

  /**
   * Get paginated list of trainings
   */
  async getTrainings(params: TrainingListParams = {}): Promise<TrainingListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.employeeId) queryParams.append('employeeId', filters.employeeId.toString());
    if (filters.departmentId) queryParams.append('departmentId', filters.departmentId.toString());
    if (filters.occupationId) queryParams.append('occupationId', filters.occupationId.toString());
    if (filters.instructorId) queryParams.append('instructorId', filters.instructorId.toString());
    if (filters.type) queryParams.append('type', filters.type);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.validityStatus) queryParams.append('validityStatus', filters.validityStatus);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.sendToEsocial !== undefined) queryParams.append('sendToEsocial', String(filters.sendToEsocial));
    if (filters.esocialStatus) queryParams.append('esocialStatus', filters.esocialStatus);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { trainings, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      trainings: backendData.trainings ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /** Emite (e na primeira vez cria o código do) certificado; devolve o caminho do PDF. */
  async emitirCertificado(id: number): Promise<{ pdfPath: string }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/certificate`);
    return response.data.data || response.data;
  }

  /**
   * Get training by ID
   */
  async getTrainingById(id: string | number): Promise<Training> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Get trainings by employee
   */
  async getTrainingsByEmployee(employeeId: number): Promise<Training[]> {
    const response = await apiClient.get(`${this.baseUrl}/employee/${employeeId}`);
    return response.data.data || response.data;
  }

  /**
   * Get trainings by company
   */
  async getTrainingsByCompany(companyId: number): Promise<Training[]> {
    const response = await apiClient.get(`${this.baseUrl}/company/${companyId}`);
    return response.data.data || response.data;
  }

  /**
   * Get trainings expiring within N days
   */
  async getExpiringTrainings(days: number = 30, companyId?: number): Promise<Training[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', days.toString());
    if (companyId) queryParams.append('companyId', companyId.toString());

    const query = queryParams.toString();
    const url = `${this.baseUrl}/expiring${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Get expired trainings
   */
  async getExpiredTrainings(companyId?: number): Promise<Training[]> {
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.append('companyId', companyId.toString());

    const query = queryParams.toString();
    const url = `${this.baseUrl}/expired${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Get training statistics for a company
   */
  async getStatistics(companyId: number): Promise<TrainingStatistics> {
    const response = await apiClient.get(`${this.baseUrl}/statistics/${companyId}`);
    return response.data.data || response.data;
  }

  /**
   * Check if employee has valid training of a type
   */
  async checkValidTraining(employeeId: number, type: TrainingNRType): Promise<{ hasValidTraining: boolean }> {
    const response = await apiClient.get(`${this.baseUrl}/check-valid/${employeeId}/${type}`);
    return response.data.data || response.data;
  }

  /**
   * Create new training
   */
  async createTraining(data: TrainingFormData): Promise<Training> {
    const payload = this.formDataToApiPayload(data);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data || response.data;
  }

  /**
   * Update training by ID
   */
  async updateTraining(id: string | number, data: Partial<TrainingFormData>): Promise<Training> {
    const payload = this.formDataToApiPayload(data as TrainingFormData);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data || response.data;
  }

  /**
   * Delete training by ID (soft delete)
   */
  async deleteTraining(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Cancel training
   */
  async cancelTraining(id: string | number): Promise<Training> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/cancel`);
    return response.data.data || response.data;
  }

  /**
   * Reactivate training
   */
  async reactivateTraining(id: string | number): Promise<Training> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/reactivate`);
    return response.data.data || response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }

  /**
   * Get default form values
   */
  getDefaultFormValues(employeeId?: number): TrainingFormData {
    const today = new Date().toISOString().split('T')[0];
    // Default 2 years from now for NR trainings
    const twoYearsLater = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    return {
      employeeId: employeeId ?? 0,
      name: '',
      category: TrainingCategory.INITIAL,
      description: '',
      activityDescription: '',
      completedAt: today,
      expiresAt: twoYearsLater,
      sendToEsocial: false,
      status: TrainingStatus.ACTIVE,
    };
  }

  /**
   * Get recommended expiry date based on training type
   */
  getRecommendedExpiryDate(type: TrainingNRType, completedAt: string): string {
    const completed = new Date(completedAt);
    let years = 2; // Default 2 years

    // Set specific validity periods based on NR requirements
    switch (type) {
      case TrainingNRType.NR_06:
      case TrainingNRType.NR_33:
        years = 1; // Annual
        break;
      case TrainingNRType.NR_10:
      case TrainingNRType.NR_35:
        years = 2; // Biennial
        break;
      case TrainingNRType.NR_20:
        years = 3; // 1-3 years (using max)
        break;
      case TrainingNRType.CIPA:
        years = 1; // Mandate period
        break;
      default:
        years = 2;
    }

    const expiryDate = new Date(completed);
    expiryDate.setFullYear(expiryDate.getFullYear() + years);
    return expiryDate.toISOString().split('T')[0];
  }

  /**
   * Get recommended workload based on training type
   */
  getRecommendedWorkload(type: TrainingNRType): number | undefined {
    switch (type) {
      case TrainingNRType.NR_10:
        return 40; // 40 hours for NR-10
      case TrainingNRType.NR_35:
        return 8; // 8 hours for NR-35
      case TrainingNRType.CIPA:
        return 20; // 20 hours for CIPA
      case TrainingNRType.FIRST_AID:
        return 8; // 8 hours typically
      default:
        return undefined;
    }
  }
}

// Export singleton instance
export const trainingService = new TrainingService();
export default trainingService;
